import { User } from '@/features/auth/store/authStore';
import { API_BASE_URL, handleResponse, apiRequest } from '@/lib/api-client';

export interface AuthUserProfile {
  id: string;
  profile_type: 'student' | 'parent';
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  preferred_name?: string | null;
  grade_level?: number | null;
  created_at: string;
  updated_at: string;
}

export interface AuthenticatedUser {
  id: string;
  username: string | null;
  email?: string | null;
  roles: ('student' | 'parent' | 'teacher' | 'admin')[];
  first_name?: string | null;
  last_name?: string | null;
  picture?: string | null;
  profile?: AuthUserProfile | null;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: AuthenticatedUser;
}

export interface RegisterRequest {
  invite_token: string;
  username: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export interface InviteValidateResponse {
  status: 'pending' | 'consumed' | 'revoked' | 'expired';
  email?: string;
  first_name?: string;
  last_name?: string;
}

export const authApi = {
  async login(username: string, password: string): Promise<LoginResponse> {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    });

    return handleResponse<LoginResponse>(response, 'Login failed');
  },

  async register(data: RegisterRequest): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    return handleResponse<LoginResponse>(response, 'Registration failed');
  },

  async googleLogin(idToken: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/google/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id_token: idToken }),
    });

    return handleResponse<LoginResponse>(response, 'Google sign-in failed');
  },

  async googleRegister(idToken: string, inviteToken: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/google/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id_token: idToken, invite_token: inviteToken }),
    });

    return handleResponse<LoginResponse>(response, 'Google registration failed');
  },

  async validateInvite(token: string): Promise<InviteValidateResponse> {
    const response = await fetch(
      `${API_BASE_URL}/auth/invites/${encodeURIComponent(token)}`,
    );
    if (response.status === 404) {
      // Treat unknown tokens as no-go; FE shows a generic invalid message.
      return { status: 'revoked' };
    }
    return handleResponse<InviteValidateResponse>(
      response,
      'Could not validate invite',
    );
  },

  async getProfile(): Promise<User> {
    return apiRequest<User>('/auth/me', {}, 'Failed to fetch profile');
  },
};
